import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Users, Factory, Building2, ArrowRight, Quote, Truck, ShieldCheck, Award } from "lucide-react";
import HeroSlider from "../components/HeroSlider";
import ProductCard from "../components/ProductCard";
import QuoteModal from "../components/QuoteModal";
import { popularProducts, hotProducts, featureCards, reasons, company } from "../data/mock";

const iconMap = { Users, Factory, Building2 };

export default function Home() {
  const [open, setOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);

  const inquire = (p) => { setActiveProduct(p); setOpen(true); };
  const generalInquire = () => { setActiveProduct(null); setOpen(true); };

  return (
    <main>
      <HeroSlider onCtaClick={generalInquire} />

      {/* Feature cards */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 -mt-16 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {featureCards.map((f) => {
            const Icon = iconMap[f.icon];
            return (
              <div key={f.id} className="bg-white rounded-lg shadow-lg p-7 border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 rounded-md bg-[#f5f6fb] flex items-center justify-center mb-4">
                  <Icon size={26} className="text-brand-red" />
                </div>
                <h3 className="text-lg font-bold text-brand-navy mb-2">{f.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{f.description}</p>
                <Link to="/company-profile" className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-brand-red hover:gap-2.5 transition-all">
                  Read More <ArrowRight size={14} />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* About / Vision */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src="https://images.pexels.com/photos/35383621/pexels-photo-35383621.jpeg" alt="Company Vision" className="w-full rounded-lg shadow-xl object-cover h-[480px]" />
            <div className="absolute -bottom-6 -right-6 bg-brand-red text-white px-6 py-4 rounded-lg shadow-lg hidden md:block">
              <div className="text-3xl font-bold">{(new Date().getFullYear()) - parseInt(company.established)}+</div>
              <div className="text-xs uppercase tracking-wider">Years of Excellence</div>
            </div>
          </div>
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-brand-red font-semibold mb-3">Company Vision</p>
            <h3 className="font-display text-3xl md:text-4xl font-bold text-brand-navy mb-3">We are Manufacturer and Supplier</h3>
            <p className="text-gray-600 mb-4">Trusted company that deals in quality-made products</p>
            <h2 className="text-2xl md:text-3xl font-bold text-brand-navy leading-snug mb-5">
              Procure The Most Relaxing &amp; Reliable Range of Memory Mattress Foam, Mattress PU Foam &amp; Foam Sheets From Us…
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Today, if a person gets ultimate comfort somewhere then it couldn’t be anywhere else other than his/her bed. The market understands customers and their requirements deeply. And, we, at <span className="font-semibold text-brand-navy">Foam Sheet Suppliers</span>, hold the potential and knowledge to meet them accordingly.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              We provide the most preferable range of <strong>Premium Quality Mattress Foam, Memory Foam, High Density Mattress Foam, FR Mattress Foam, EPE Foam Sheets</strong> and many other products across India.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/company-profile" className="btn-primary-brand px-6 py-3 rounded-md text-sm font-semibold">Read More</Link>
              <button onClick={generalInquire} className="px-6 py-3 rounded-md text-sm font-semibold border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white transition-colors">
                Request a Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Facts */}
      <section className="bg-white py-16 border-y border-gray-100 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(#1f2444 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
          <div className="text-center mb-10">
            <p className="text-sm uppercase tracking-[0.3em] text-brand-red font-semibold mb-2">Key Facts</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-navy">Key Facts of Foam Sheet Suppliers</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { v: company.established, l: "Year of Establishment" },
              { v: company.employees, l: "No. of Employees" },
              { v: company.productionUnits, l: "No. of Production Units" },
              { v: company.branches, l: "Company Branch" },
            ].map((s, i) => (
              <div key={i} className="text-center px-2">
                <div className="text-5xl md:text-6xl font-extrabold text-brand-red">{s.v}</div>
                <div className="text-sm md:text-base text-brand-navy font-semibold mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reasons to Buy */}
      <section className="brand-navy text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 dotted-bg pointer-events-none" />
        <div className="absolute inset-0 circles-bg pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold">Reasons to Buy From US!</h2>
            <p className="text-sm text-white/60 mt-2">What sets Foam Sheet Suppliers apart from the rest</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {reasons.map((r) => (
              <div key={r.id} className="reasons-card group rounded-lg overflow-hidden h-56 cursor-pointer">
                <img src={r.image} alt={r.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 overlay flex items-end p-6">
                  <h3 className="text-lg font-bold leading-snug">{r.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-brand-red font-semibold mb-3">About Us</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-navy mb-5">
              Crafting Comfort, Engineering Trust
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              By working as a <strong>manufacturer</strong>, we are serving the most comfortable, long-lasting and qualitative range of foam sheet products in the market nationwide. Our range possesses the quality which can easily enhance the comfort level of any bed or industrial application.
            </p>
            <ul className="space-y-3 mb-6">
              {[
                { i: ShieldCheck, t: "ISO certified manufacturing process" },
                { i: Truck, t: "Pan-India delivery with bulk packaging" },
                { i: Award, t: "Premium-grade raw materials only" },
              ].map(({ i: Icon, t }, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-[#f5f6fb] flex items-center justify-center shrink-0">
                    <Icon size={18} className="text-brand-red" />
                  </div>
                  <span className="text-gray-700 text-sm pt-1.5">{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/company-profile" className="btn-primary-brand inline-flex px-6 py-3 rounded-md text-sm font-semibold">Read More</Link>
          </div>
          <img src="https://images.unsplash.com/photo-1605874562151-24fc26211285?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHx3YXJlaG91c2UlMjBmYWN0b3J5fGVufDB8fHx8MTc3NzE3NDY0MHww&ixlib=rb-4.1.0&q=85" alt="About Us" className="w-full h-[480px] object-cover rounded-lg shadow-xl" />
        </div>
      </section>

      {/* Most Popular Products */}
      <section id="products" className="bg-[#f7f8fc] py-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="text-sm uppercase tracking-[0.3em] text-brand-red font-semibold mb-2">Most Popular Products</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-navy">Experience Perfection in well-designed products</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {popularProducts.map((p) => (
              <ProductCard key={p.id} product={p} onInquire={inquire} />
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/products" className="btn-primary-brand inline-flex px-7 py-3 rounded-md text-sm font-semibold">View All Products</Link>
          </div>
        </div>
      </section>

      {/* Sleep Soft Banner */}
      <section className="relative h-[420px] overflow-hidden">
        <img src="https://images.pexels.com/photos/7227620/pexels-photo-7227620.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Comfort" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1f2444]/85 to-[#1f2444]/30" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 w-full text-white">
            <Quote size={36} className="text-brand-red mb-4" />
            <h3 className="font-display text-3xl md:text-5xl font-bold leading-tight max-w-3xl">Comfortable Mattress Foam</h3>
            <p className="font-display text-2xl md:text-3xl text-brand-red mt-2 italic">Sleep Soft. Live Better.</p>
            <button onClick={generalInquire} className="btn-red-brand mt-6 px-7 py-3 rounded-md text-sm font-semibold">
              Get a Quote
            </button>
          </div>
        </div>
      </section>

      {/* Hot Products */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="text-sm uppercase tracking-[0.3em] text-brand-red font-semibold mb-2">Hot Products</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-brand-navy">Trending in our catalog</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {hotProducts.map((p) => (
              <ProductCard key={p.id} product={p} onInquire={inquire} />
            ))}
          </div>
        </div>
      </section>

      {/* Quote CTA */}
      <section id="quote" className="brand-navy text-white py-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-bold">Don’t hesitate to contact us for more information.</h3>
            <p className="text-white/70 mt-1 text-sm">Get the best wholesale prices on all foam sheet products.</p>
          </div>
          <div className="flex gap-3">
            <button onClick={generalInquire} className="btn-red-brand px-7 py-3 rounded-md text-sm font-semibold">Request A Quote</button>
            <Link to="/contact-us" className="px-7 py-3 rounded-md text-sm font-semibold border border-white/30 hover:bg-white hover:text-brand-navy transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>

      <QuoteModal open={open} onOpenChange={setOpen} productName={activeProduct?.name} />
    </main>
  );
}
